import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from '@/context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Search,
  Package,
  Users,
  ShoppingCart,
  BarChart3,
  Wallet,
  MapPin,
  ClipboardList,
  Truck,
  Settings,
  Building2,
  Calendar,
  X,
} from 'lucide-react';

interface CommandItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  keywords: string[];
  roles?: string[];
}

const getAllCommands = (t: (key: string) => string): CommandItem[] => [
  // Main
  {
    id: 'dashboard',
    label: t('cmd.nav.dashboard'),
    path: '/dashboard',
    icon: <BarChart3 size={16} />,
    keywords: ['главная', 'dashboard', 'home', 'аналитика'],
  },
  {
    id: 'orders',
    label: t('cmd.nav.orders'),
    path: '/orders',
    icon: <ShoppingCart size={16} />,
    keywords: ['заказ', 'order', 'продажа'],
  },
  {
    id: 'orders-new',
    label: t('cmd.nav.new_order'),
    path: '/orders/new',
    icon: <ShoppingCart size={16} />,
    keywords: ['новый заказ', 'создать заказ', 'new order'],
  },
  {
    id: 'visits',
    label: t('cmd.nav.visits'),
    path: '/visits',
    icon: <MapPin size={16} />,
    keywords: ['визит', 'visit', 'посещение'],
  },
  {
    id: 'clients',
    label: t('cmd.nav.clients_w'),
    path: '/clients/wholesale',
    icon: <Users size={16} />,
    keywords: ['клиент', 'client', 'wholesale', 'опт'],
  },
  {
    id: 'clients-retail',
    label: t('cmd.nav.clients_r'),
    path: '/clients/retail',
    icon: <Users size={16} />,
    keywords: ['розница', 'retail'],
  },
  {
    id: 'tasks',
    label: t('cmd.nav.tasks'),
    path: '/tasks',
    icon: <ClipboardList size={16} />,
    keywords: ['задача', 'task', 'todo'],
  },
  {
    id: 'logistics',
    label: t('cmd.nav.logistics'),
    path: '/logistics',
    icon: <Truck size={16} />,
    keywords: ['логистика', 'доставка', 'delivery'],
  },

  // Inventory
  {
    id: 'products',
    label: t('cmd.nav.products'),
    path: '/inventory/products',
    icon: <Package size={16} />,
    keywords: ['товар', 'product', 'продукт', 'каталог'],
  },
  {
    id: 'pricing',
    label: t('cmd.nav.pricing'),
    path: '/inventory/pricing',
    icon: <Package size={16} />,
    keywords: ['прайс', 'цена', 'price'],
  },
  {
    id: 'stocks',
    label: t('cmd.nav.stocks'),
    path: '/inventory/stocks',
    icon: <Package size={16} />,
    keywords: ['остаток', 'stock', 'склад', 'инвентарь'],
  },
  {
    id: 'locations',
    label: t('cmd.nav.warehouses'),
    path: '/inventory/locations',
    icon: <Building2 size={16} />,
    keywords: ['склад', 'location', 'warehouse'],
  },

  // Warehouse
  {
    id: 'transfers',
    label: t('cmd.nav.transfers'),
    path: '/warehouse/transfers',
    icon: <Truck size={16} />,
    keywords: ['перемещение', 'transfer', 'трансфер'],
  },
  {
    id: 'purchases',
    label: t('cmd.nav.purchases'),
    path: '/warehouse/purchases',
    icon: <ShoppingCart size={16} />,
    keywords: ['закупка', 'purchase', 'приход'],
  },

  // Finance
  {
    id: 'finance',
    label: t('cmd.nav.finance'),
    path: '/finance',
    icon: <Wallet size={16} />,
    keywords: ['финанс', 'касса', 'finance', 'деньги', 'оплата'],
  },
  {
    id: 'installments',
    label: t('cmd.nav.installments'),
    path: '/finance/installments',
    icon: <Wallet size={16} />,
    keywords: ['рассрочка', 'installment', 'кредит'],
  },

  // HR
  {
    id: 'employees',
    label: t('cmd.nav.employees'),
    path: '/hr/employees',
    icon: <Users size={16} />,
    keywords: ['сотрудник', 'employee', 'персонал', 'hr'],
  },
  {
    id: 'attendance',
    label: t('cmd.nav.attendance'),
    path: '/hr/attendance',
    icon: <Calendar size={16} />,
    keywords: ['посещаемость', 'attendance', 'face id'],
  },
  {
    id: 'shifts',
    label: t('cmd.nav.shifts'),
    path: '/hr/shifts',
    icon: <Calendar size={16} />,
    keywords: ['смена', 'shift'],
  },

  // Reference
  {
    id: 'branches',
    label: t('cmd.nav.branches'),
    path: '/reference/branches',
    icon: <Building2 size={16} />,
    keywords: ['филиал', 'branch', 'офис'],
    roles: ['ADMIN', 'DIRECTOR'],
  },
  {
    id: 'categories',
    label: t('cmd.nav.categories'),
    path: '/reference/categories',
    icon: <Settings size={16} />,
    keywords: ['категория', 'category'],
  },
  {
    id: 'brands',
    label: t('cmd.nav.brands'),
    path: '/reference/brands',
    icon: <Settings size={16} />,
    keywords: ['бренд', 'brand', 'производитель'],
  },
  {
    id: 'units',
    label: t('cmd.nav.units'),
    path: '/reference/units',
    icon: <Settings size={16} />,
    keywords: ['единица', 'unit', 'измерение'],
  },

  // Analytics
  {
    id: 'analytics',
    label: t('cmd.nav.analytics'),
    path: '/analytics',
    icon: <BarChart3 size={16} />,
    keywords: ['аналитика', 'analytics', 'отчёт', 'report'],
  },
  {
    id: 'settings',
    label: t('cmd.nav.settings'),
    path: '/settings',
    icon: <Settings size={16} />,
    keywords: ['настройка', 'setting', 'конфиг'],
    roles: ['ADMIN'],
  },
];

export default function CommandPalette() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Filter commands based on user role
  const commands = useMemo(() => {
    if (!user) return [];
    return getAllCommands(t).filter((cmd) => {
      if (!cmd.roles) return true;
      return cmd.roles.includes(user.role) || user.role === 'ADMIN' || user.role === 'DIRECTOR';
    });
  }, [user, t]);

  // Filter by search query
  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter((cmd) => cmd.label.toLowerCase().includes(q) || cmd.keywords.some((kw) => kw.includes(q)));
  }, [commands, query]);

  // Keyboard shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const handleCustomOpen = () => setOpen(true);

    document.addEventListener('keydown', handler);
    document.addEventListener('open-command-palette', handleCustomOpen);

    return () => {
      document.removeEventListener('keydown', handler);
      document.removeEventListener('open-command-palette', handleCustomOpen);
    };
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const item = listRef.current.children[selectedIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (path: string) => {
      navigate(path);
      setOpen(false);
    },
    [navigate],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      handleSelect(filtered[selectedIndex].path);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-100 flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-xs" />

      {/* Palette */}
      <div
        className="relative w-full max-w-lg rounded-sm border border-border bg-card shadow-2xl animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search size={16} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Поиск по модулям..."
            className="flex-1 bg-transparent py-3 text-sm text-foreground placeholder:text-muted-foreground outline-hidden"
          />
          <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
            <X size={14} />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[300px] overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">{t('cmd.no_results')}</div>
          ) : (
            filtered.map((cmd, i) => (
              <button
                key={cmd.id}
                onClick={() => handleSelect(cmd.path)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                  i === selectedIndex ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50'
                }`}
              >
                <span className="text-muted-foreground">{cmd.icon}</span>
                <span>{cmd.label}</span>
                <span className="ml-auto text-xs text-muted-foreground">{cmd.path}</span>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 border-t border-border px-3 py-2 text-xs text-muted-foreground">
          <span>↑↓ навигация</span>
          <span>↵ выбрать</span>
          <span>esc закрыть</span>
        </div>
      </div>
    </div>
  );
}
